import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { Box, Flex, Text } from '../../ui-kit';
import CarouselChevronRightIcon from '../../ui-kit/icons/CarouselChevronRigthThin';
import theme from '../../ui-kit/themes/theme';
import { LOYALTY_IMAGES } from '../assets/images';
import { Icon, LoyaltyAppliedContainer } from '../assets/styles';
import * as COPY from '../utilities/copy';
import {
  editCopyByReplaceString,
  editCopyByReplaceStringWithBold,
  editCopyByReplaceWithComponent
} from './../../utilities/copyFunctions';

const calculatePointsToEarn = ({
  userOffers,
  activeOffers,
  pointsBalance,
  suitNightRate,
  currentSelectedPackages,
  pointsMultiplier
}) => {
  let total = 0;

  // USE VOYAGERS POINTS
  let roomRateWithPoints = 0;
  if (!pointsBalance || pointsBalance === 0) {
    roomRateWithPoints = suitNightRate;
  } else {
    if (pointsBalance > suitNightRate) {
      roomRateWithPoints = 0;
    } else {
      roomRateWithPoints = suitNightRate - pointsBalance;
    }
  }

  // USE PACKAGES
  let packagesTotal = 0;

  if (currentSelectedPackages && currentSelectedPackages.length > 0) {
    currentSelectedPackages.forEach(currentPackage => {
      if (currentPackage.amount && currentPackage.quantity) {
        let calculateQuantityPrice = currentPackage.amount * currentPackage.quantity;
        packagesTotal = packagesTotal + calculateQuantityPrice;
      }
    });
  }

  // USE OFFERS
  let offersSavings = 0;
  if (userOffers && userOffers.length > 0) {
    offersSavings = userOffers.reduce((previousValue, currentValue) => {
      if (activeOffers.length > 0 && activeOffers.includes(currentValue.id)) {
        return previousValue + currentValue.dollarValue;
      } else {
        return previousValue;
      }
    }, 0);
  }

  // USE MULTIPLIER
  total = roomRateWithPoints + packagesTotal - offersSavings;
  let points = total * pointsMultiplier;
  if (points < 0) return 0;
  return points.toFixed(0);
};

const LoyaltyEarn = ({
  iconLink,
  numberOfNights,
  isUserAuthenticated,
  suitNightRate,
  pointsMultiplier,
  userOffers,
  activeOffers,
  currentSelectedPackages,
  pointsBalance,
  arrowVariation,
  loyaltyEarnThresholdNights = 2,
  loyaltyRedesign2025Active
}) => {
  const points =
    suitNightRate && suitNightRate > 0
      ? calculatePointsToEarn({
          suitNightRate,
          pointsMultiplier,
          currentSelectedPackages,
          pointsBalance,
          userOffers,
          activeOffers
        })
      : 0;

  const openSignUp = useCallback(() => {
    if (!isUserAuthenticated && window.openSignUp) {
      window.openSignUp('sign-up-voyagers');
    }
  }, [isUserAuthenticated]);

  const pointsToEarn = () => {
    return <Text as="strong">{editCopyByReplaceString(COPY.NUMBER_POINTS, points, '<TOTALPOINTS>')}</Text>;
  };

  const joinVoyagerUnderline = () => {
    return (
      <Text as="span" underline={true} fontWeight="bold" capitalize>
        {COPY.JOIN_VOYAGERS}
      </Text>
    );
  };

  return (
    <LoyaltyAppliedContainer
      loyaltyRedesign2025Active={loyaltyRedesign2025Active}
      isUserAuthenticated={isUserAuthenticated}
      alignItems="center"
      enablePadding
      onClick={openSignUp}>
      <Icon
        size={loyaltyRedesign2025Active ? '24px' : '20px'}
        src={loyaltyRedesign2025Active ? LOYALTY_IMAGES.ADVENTURE_BUCK_COIN_GREEN : iconLink}
        alt="Loyalty"
        alignSelf="flex-start"
      />
      <Box ml={2}>
        {isUserAuthenticated && numberOfNights >= loyaltyEarnThresholdNights && (
          <Flex alignItems={arrowVariation ? 'center' : 'left'}>
            <Box width={arrowVariation ? '681px' : 'auto'}>
              <Text
                lineHeight="20px"
                fontSize={1}
                fontWeight="medium"
                my="0"
                color={loyaltyRedesign2025Active && theme.colors.pureWhite[0]}>
                {editCopyByReplaceWithComponent(COPY.YOU_WILL_EARN, pointsToEarn(), '<POINTS>')}
              </Text>
              <Text
                fontSize={0}
                fontWeight="medium"
                mt="2"
                mb="0"
                lineHeight="18px"
                color={loyaltyRedesign2025Active && theme.colors.pureWhite[0]}>
                {COPY.POINTS_EARNED_UPPON_COMPLETION}
              </Text>
            </Box>
            {arrowVariation ? (
              <Flex width="24px" height="24px" justifyContent="center" alignItems="center">
                <CarouselChevronRightIcon size={20}></CarouselChevronRightIcon>
              </Flex>
            ) : null}
          </Flex>
        )}
        {isUserAuthenticated && numberOfNights < loyaltyEarnThresholdNights && (
          <Flex alignItems={arrowVariation ? 'center' : 'left'}>
            <Box width={arrowVariation ? '681px' : 'auto'}>
              <Text lineHeight="20px" fontSize={1} fontWeight="medium" my="0" color={theme.colors.pureWhite[0]}>
                {editCopyByReplaceStringWithBold(COPY.ADD_MORE_NIGHTS, COPY.VOYAGERS_POINTS, '<POINTS>')}
              </Text>
              <Text fontSize={0} fontWeight="medium" mt="2" mb="0" color={theme.colors.pureWhite[0]} lineHeight="18px">
                {COPY.POINTS_EARNED_UPPON_COMPLETION}
              </Text>
            </Box>
            {arrowVariation ? (
              <Flex width="24px" height="24px" justifyContent="center" alignItems="center">
                <CarouselChevronRightIcon size={20}></CarouselChevronRightIcon>
              </Flex>
            ) : null}
          </Flex>
        )}
        {!isUserAuthenticated && !arrowVariation && numberOfNights < loyaltyEarnThresholdNights && (
          <Text lineHeight="20px" fontSize={1} fontWeight="medium" my="0" color={theme.colors.pureWhite[0]}>
            <Text as="span" underline={true} fontWeight="bold">
              {COPY.JOIN_VOYAGERS}
            </Text>
            {editCopyByReplaceStringWithBold(COPY.JOIN_COPY, COPY.VOYAGERS_POINTS, '<JOIN>')}
          </Text>
        )}
        {!isUserAuthenticated && !arrowVariation && numberOfNights >= loyaltyEarnThresholdNights && (
          <>
            <Text lineHeight="20px" fontSize={1} fontWeight="medium" my="0" color={theme.colors.pureWhite[0]}>
              <Text as="span" underline={true} fontWeight="bold">
                {COPY.JOIN_VOYAGERS}
              </Text>{' '}
              {COPY.EARN_BY_JOIN}
            </Text>
            <Text fontSize={0} fontWeight="medium" mt="2" mb="0" color={theme.colors.pureWhite[0]} lineHeight="18px">
              {COPY.POINTS_EARNED_UPPON_COMPLETION}
            </Text>
          </>
        )}
        {!isUserAuthenticated && arrowVariation ? (
          <Flex alignItems="center">
            <Box width="681px">
              <Text fontSize={1} fontWeight="bold" color={theme.colors.pureWhite[0]} lineHeight={18}>
                {editCopyByReplaceWithComponent(
                  COPY.JOIN_VOYAGERS_CLUB_COPY,
                  joinVoyagerUnderline(),
                  '<JOIN_UNDERLINE>'
                )}
              </Text>
              <Text fontSize={1} fontWeight="medium" color={theme.colors.pureWhite[0]} lineHeight={18}>
                {COPY.POINTS_EARNED_UPPON_COMPLETION}
              </Text>
            </Box>
            <Flex width="24px" height="24px" justifyContent="center" alignItems="center">
              <CarouselChevronRightIcon size={20}></CarouselChevronRightIcon>
            </Flex>
          </Flex>
        ) : null}
      </Box>
    </LoyaltyAppliedContainer>
  );
};

LoyaltyEarn.propTypes = {
  iconLink: PropTypes.string,
  numberOfNights: PropTypes.number
};
LoyaltyEarn.defaultProps = {
  iconLink: LOYALTY_IMAGES.ADVENTURE_BUCK_COIN_WHITE,
  numberOfNights: 1
};

export default LoyaltyEarn;
